/**
 * Exportación de la agenda a iCalendar (.ics), para importarla en Google Calendar,
 * Apple Calendar u Outlook. Es una copia: los cambios posteriores no se sincronizan.
 * Las horas se escriben como hora local "flotante" (sin zona), que los calendarios
 * interpretan en la zona del calendario de destino.
 */
import type { LocalDate } from '@habitos/engine';
import type { WeekDay } from './agenda';

const CRLF = '\r\n';

function escape(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

/** Las líneas de más de 75 octetos se pliegan con un espacio al inicio de la siguiente (RFC 5545). */
function fold(line: string): string {
  const bytes = new TextEncoder().encode(line);
  if (bytes.length <= 75) return line;
  const parts: string[] = [];
  let current = '';
  for (const ch of line) {
    if (new TextEncoder().encode(current + ch).length > (parts.length ? 74 : 75)) {
      parts.push(current);
      current = ch;
    } else current += ch;
  }
  parts.push(current);
  return parts.join(`${CRLF} `);
}

const compactDate = (d: LocalDate) => d.replace(/-/g, '');
const compactTime = (t: string) => `${t.replace(':', '')}00`;

function stamp(now: Date): string {
  return now.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

export function buildIcs(week: WeekDay[], opts: { name: string; now?: Date }): string {
  const now = stamp(opts.now ?? new Date());
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//HabitOS//Agenda//ES', 'CALSCALE:GREGORIAN', `X-WR-CALNAME:${escape(opts.name)}`];
  for (const { day, entries } of week) {
    for (const e of entries) {
      // Hábitos sin hora no se exportan: llenarían el calendario de eventos de día completo.
      if (e.kind === 'habit' && !e.time) continue;
      lines.push('BEGIN:VEVENT', `UID:${e.key}-${compactDate(day)}@habitos`, `DTSTAMP:${now}`, `SUMMARY:${escape(e.title)}`);
      if (e.time && e.end) {
        lines.push(`DTSTART:${compactDate(day)}T${compactTime(e.time)}`, `DTEND:${compactDate(day)}T${compactTime(e.end)}`);
      } else {
        lines.push(`DTSTART;VALUE=DATE:${compactDate(day)}`);
      }
      if (e.done) lines.push('STATUS:CONFIRMED');
      lines.push('END:VEVENT');
    }
  }
  lines.push('END:VCALENDAR');
  return lines.map(fold).join(CRLF) + CRLF;
}
